import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import PropTypes from 'prop-types';
import { getNewYorkTimeZoneDate } from '../../../utils/dayjsUs';

export function EventDialog({
  open,
  onClose,
  onCreate,
  onUpdate,
  onDelete,
  formData,
  setFormData,
  dialogMode,
}) {
  const [isDisabled, setIsDisabled] = useState(true);
  const handleDialogChange = (prop) => (event) => {
    setFormData({ ...formData, [prop]: event.target.value });
  };
 
  const handleDialogDateChange = (prop) => (value) => {
    setFormData({ ...formData, [prop]: value });
  };

  useEffect(() => {
    if (
      !formData.name ||
      (formData.type === 'meals' && !formData.mealType) ||
      !getNewYorkTimeZoneDate(formData.date).isValid() ||
      (formData.type !== 'meals' &&
        !getNewYorkTimeZoneDate(formData.timeFrom).isValid())
    ) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  }, [formData]);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        component: 'form',
      }}
    >
      <DialogContent>
        <Box sx={{ marginBottom: 2 }}>
          <ToggleButtonGroup
            value={formData.type}
            onChange={handleDialogChange('type')}
            exclusive
            required
            disabled={dialogMode === 'edit'}
          >
            <ToggleButton value="activities">Activity</ToggleButton>
            <ToggleButton value="meals">Meal</ToggleButton>
            <ToggleButton value="memoryCareActivities">
              Memory Care
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {formData.type === 'meals' && (
          <Box sx={{ marginBottom: 2 }}>
            <FormControl fullWidth required>
              <InputLabel
                id="meal-type-label"
                error={!formData.mealType}
                helperText={!formData.mealType ? 'Meal Type is required' : ''}
              >
                Meal Type
              </InputLabel>
              <Select
                labelId="meal-type-label"
                label="Meal Type"
                value={formData.mealType || ''}
                onChange={handleDialogChange('mealType')}
                required
              >
                <MenuItem value="breakfast">Breakfast</MenuItem>
                <MenuItem value="lunch">Lunch</MenuItem>
                <MenuItem value="dinner">Dinner</MenuItem>
                <MenuItem value="alternate">Alternates</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
        <Box sx={{ marginBottom: 2 }}>
          <TextField
            error={formData.name === ''}
            helperText={
              formData.name === ''
                ? formData.type === 'activities'
                  ? 'Activity is required'
                  : formData.type === 'meals'
                    ? 'Meal is required'
                    : formData.type === 'memoryCareActivities'
                      ? 'Memory Care Activity is required'
                      : ''
                : ''
            }
            label={
              formData.type === 'activities'
                ? 'Activity'
                : formData.type === 'meals'
                  ? 'Meal'
                  : formData.type === 'memoryCareActivities'
                    ? 'Memory Care Activity'
                    : ''
            }
            fullWidth
            multiline
            minRows={
              formData.type === 'activities' ||
              formData.type === 'memoryCareActivities'
                ? 1
                : 3
            }
            value={formData.name}
            onChange={handleDialogChange('name')}
            required
          />
        </Box>
        <Box sx={{ marginBottom: 2 }}>
          <DatePicker
            views={
              formData.mealType === 'alternate'
                ? ['year', 'month']
                : ['year', 'month', 'day']
            }
            label="Date"
            value={formData.date}
            onChange={handleDialogDateChange('date')}
            timezone="America/New_York"
            required
            error={
              !formData.date || !getNewYorkTimeZoneDate(formData.date).isValid()
            }
            slotProps={{
              textField: {
                error:
                  !formData.date ||
                  !getNewYorkTimeZoneDate(formData.date).isValid(),
                helperText: !formData.date
                  ? 'Date is required'
                  : !getNewYorkTimeZoneDate(formData.date).isValid()
                    ? 'Invalid date'
                    : '',
              },
            }}
          />
        </Box>
        {formData.type === 'activities' && (
          <>
            <Box sx={{ marginBottom: 2 }}>
              <TimePicker
                label="From"
                value={formData.timeFrom}
                onChange={handleDialogDateChange('timeFrom')}
                timezone="America/New_York"
                required
                error={
                  !formData.timeFrom ||
                  !getNewYorkTimeZoneDate(formData.timeFrom).isValid()
                }
                slotProps={{
                  textField: {
                    error:
                      !formData.timeFrom ||
                      !getNewYorkTimeZoneDate(formData.timeFrom).isValid(),
                    helperText: !formData.timeFrom
                      ? 'Time is required'
                      : !getNewYorkTimeZoneDate(formData.timeFrom).isValid()
                        ? 'Invalid time'
                        : '',
                  },
                }}
              />
            </Box>
            <Box sx={{ marginBottom: 2 }}>
              <TimePicker
                label="To"
                value={formData.timeTo}
                onChange={handleDialogDateChange('timeTo')}
                timezone="America/New_York"
              />
            </Box>
          </>
        )}
        {formData.type === 'memoryCareActivities' && (
          <>
            <Box sx={{ marginBottom: 2 }}>
              <TimePicker
                label="From"
                value={formData.timeFrom}
                onChange={handleDialogDateChange('timeFrom')}
                timezone="America/New_York"
              />
            </Box>
            <Box sx={{ marginBottom: 2 }}>
              <TimePicker
                label="To"
                value={formData.timeTo}
                onChange={handleDialogDateChange('timeTo')}
                timezone="America/New_York"
              />
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="error" onClick={onClose}>
          Cancel
        </Button>
        {dialogMode === 'edit' && (
          <Button color="secondary" onClick={() => onDelete()}>
            Delete
          </Button>
        )}

        {dialogMode === 'edit' && (
          <Button
            disabled={isDisabled}
            color="secondary"
            onClick={() => onUpdate()}
          >
            Update
          </Button>
        )}
        {dialogMode !== 'edit' && (
          <Button
            disabled={isDisabled}
            color="secondary"
            onClick={() => onCreate()}
          >
            Create
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

EventDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  formData: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    date: PropTypes.object,
    timeFrom: PropTypes.object,
    timeTo: PropTypes.object,
    type: PropTypes.oneOf(['activities', 'meals', 'memoryCareActivities']),
    mealType: PropTypes.oneOf(['breakfast', 'lunch', 'dinner', 'alternate']),
  }).isRequired,
  setFormData: PropTypes.func.isRequired,
  dialogMode: PropTypes.oneOf(['create', 'edit']).isRequired,
};
